import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, Check } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MediaSlot from "@/components/MediaSlot";
import PhoneInput, { Honeypot, isValidPhone } from "@/components/PhoneInput";
import { useLang } from "@/i18n";
import { api, track, formatApiError, getSrc } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { JOIN, AVAIL, WORK } from "@/data/join";
import { IconGrid } from "@/components/IconArt";
import ReviewVideo from "@/components/ReviewVideo";

const inputCls = "w-full h-12 px-4 rounded-xl border border-[#E5E0D8] bg-white text-[#121316] outline-none focus:border-[#E05624] focus:ring-2 focus:ring-[#E05624]/20 transition";
const H2 = "font-display font-bold tracking-tight text-3xl sm:text-4xl text-[#121316]";

function Earnings({ c, rate, share, cur }) {
  const [hpd, setHpd] = useState(4);
  const [dpw, setDpw] = useState(5);
  const perHour = rate * share;
  const week = perHour * hpd * dpw;
  const fmt = (n) => `${cur}${Math.round(n).toLocaleString("en-IN")}`;
  return (
    <section className="chela-section">
      <div className="chela-container max-w-4xl">
        <h2 className={H2}>{c.calc_t}</h2>
        <div data-testid="earnings-calc" className="mt-8 chela-card rounded-3xl p-6 sm:p-8" style={{ transform: "none" }}>
          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <label className="block"><span className="block text-sm font-bold mb-1.5">{c.hours_day}</span>
              <select data-testid="earn-hours" value={hpd} onChange={(e) => setHpd(Number(e.target.value))} className={inputCls}>{[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n}>{n}</option>)}</select></label>
            <label className="block"><span className="block text-sm font-bold mb-1.5">{c.days_week}</span>
              <select data-testid="earn-days" value={dpw} onChange={(e) => setDpw(Number(e.target.value))} className={inputCls}>{[1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n}>{n}</option>)}</select></label>
          </div>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[[c.per_hour, perHour, "hour"], [c.per_week, week, "week"], [c.per_month, week * 4, "month"]].map(([l, v, k]) => (
              <div key={k} className="rounded-2xl bg-[#FFF1EC] p-4">
                <div data-testid={`earn-${k}`} className="font-display font-extrabold text-2xl sm:text-3xl text-[#E05624]">{fmt(v)}</div>
                <div className="text-xs font-semibold text-[#5A5D65] mt-1">{l}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm font-semibold text-[#121316]">{c.tip}</p>
        </div>
      </div>
    </section>
  );
}

function Chips({ label, keys, labels, value, onChange, testid }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div><span className="block text-sm font-bold text-[#121316] mb-2">{label}</span>
      <div className="flex flex-wrap gap-2">
        {keys.map((k) => (
          <button type="button" key={k} data-testid={`${testid}-${k}`} onClick={() => toggle(k)} className={`h-10 px-4 rounded-full text-sm font-semibold border transition-colors ${value.includes(k) ? "bg-[#121316] text-white border-[#121316]" : "bg-white border-[#E5E0D8] text-[#3A3D45]"}`}>{labels[k]}</button>
        ))}
      </div>
    </div>
  );
}

function Tick({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input data-testid={testid} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-5 w-5 accent-[#E05624]" />
      <span className="text-sm font-semibold text-[#121316]">{label}</span>
    </label>
  );
}

const EMPTY = { name: "", whatsapp: "", areas: "", work_types: [], availability: [], is_adult: false, consent: false, website: "" };

function ApplyForm({ c, t }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [phoneErr, setPhoneErr] = useState("");
  const put = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!isValidPhone(form.whatsapp)) { setPhoneErr(t("phone_err")); return; }
    setPhoneErr("");
    if (!form.work_types.length) { toast.error(c.need_work); return; }
    if (!form.is_adult || !form.consent) { toast.error(c.need_checks); return; }
    setBusy(true);
    try {
      await api.post("/workers", { ...form, src: getSrc() });
      track("worker_apply", { areas: form.areas });
      setDone(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (done) return <div data-testid="join-success" className="chela-card rounded-3xl p-8 text-center" style={{ transform: "none" }}><Check size={32} className="mx-auto text-[#1B8A4B]" /><p className="mt-4 font-display font-bold text-xl">{c.success}</p></div>;
  return (
    <form data-testid="join-form" onSubmit={submit} className="chela-card rounded-3xl p-6 sm:p-8 space-y-5" style={{ transform: "none" }}>
      <p className="text-xs font-semibold text-[#5A5D65]">{c.required_note}</p>
      <div className="grid sm:grid-cols-2 gap-5">
        <label className="block"><span className="block text-sm font-bold text-[#121316] mb-1.5">{c.f.name}</span>
          <input data-testid="join-name" value={form.name} onChange={(e) => put("name")(e.target.value)} required className={inputCls} /></label>
        <div><span className="block text-sm font-bold text-[#121316] mb-1.5">{c.f.whatsapp}</span>
          <PhoneInput testid="join-whatsapp" value={form.whatsapp} onChange={put("whatsapp")} error={phoneErr} /></div>
      </div>
      <label className="block"><span className="block text-sm font-bold text-[#121316] mb-1.5">{c.f.areas}</span>
        <input data-testid="join-areas" value={form.areas} onChange={(e) => put("areas")(e.target.value)} placeholder={c.f.areas_ph} required className={inputCls} /></label>
      <Chips label={c.f.work} keys={WORK} labels={c.work} value={form.work_types} onChange={put("work_types")} testid="join-work" />
      <Chips label={c.f.availability} keys={AVAIL} labels={c.avail} value={form.availability} onChange={put("availability")} testid="join-avail" />
      <Tick testid="join-adult" checked={form.is_adult} onChange={put("is_adult")} label={c.adult} />
      <Tick testid="join-consent" checked={form.consent} onChange={put("consent")} label={c.consent} />
      <p className="text-xs text-[#5A5D65]">{c.no_id}</p>
      <Honeypot value={form.website} onChange={put("website")} />
      <button data-testid="join-submit" type="submit" disabled={busy} className="w-full h-14 rounded-full btn-saffron font-bold text-base disabled:opacity-60">{busy ? "..." : c.submit}</button>
      <p className="text-xs text-center text-[#5A5D65]"><Link data-testid="join-terms-link" to="/terms" className="underline">{t("link_terms")}</Link> · <Link data-testid="join-privacy-link" to="/privacy" className="underline">{t("link_privacy")}</Link></p>
    </form>
  );
}

export default function Join() {
  const { lang, t } = useLang();
  const s = useSettings();
  const c = JOIN[lang] || JOIN.en;
  const rate = s.base_hourly_price;
  const min = `${s.currency}${Math.floor((rate * s.worker_share) / 10) * 10}`;
  const toForm = () => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });
  useEffect(() => { track("page_view", { page: "join" }); }, []);

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header />
      <section className="chela-container pt-10 sm:pt-16 pb-12">
        <h1 data-testid="join-title" className="font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-[#121316] max-w-3xl">{c.h1}</h1>
        <p className="mt-5 text-lg sm:text-xl text-[#5A5D65] max-w-2xl">{c.intro.replace("{min}", min)}</p>
        <button data-testid="join-apply-btn" onClick={toForm} className="mt-8 inline-flex items-center gap-2 h-14 px-8 rounded-full btn-saffron text-base font-bold">{c.cta} <ArrowDown size={18} /></button>
      </section>

      <section className="chela-section bg-[#121316] text-[#FAF8F5]">
        <div className="chela-container max-w-3xl">
          <h2 className="font-display font-bold text-3xl sm:text-4xl">{c.video_t}</h2>
          <div className="mt-8 rounded-3xl overflow-hidden aspect-video bg-black">
            <MediaSlot slot={s.media?.worker_video} fallback={<IconGrid kinds={["call", "go", "help", "pay"]} lang={lang} className="w-full h-full" />} mode="tap" alt={c.video_t} className="w-full h-full object-cover" testid="worker-video" />
          </div>
          <p data-testid="worker-video-caption" className="mt-4 font-semibold leading-relaxed">Kaam khatam. Payment seedha UPI mein. CHELA ke saath kamaao.</p>
          <p className="mt-1 text-white/80 leading-relaxed">{c.caption.replace("{min}", min)}</p>
        </div>
      </section>

      <Earnings c={c} rate={rate} share={s.worker_share} cur={s.currency} />

      <ReviewVideo kind="worker" />

      <section className="chela-section bg-[#F3EFEA]">
        <div className="chela-container">
          <h2 className={H2}>{c.steps_t}</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {c.steps.map((st, i) => (
              <div key={i} data-testid={`join-step-${i + 1}`} className="chela-card rounded-3xl p-6" style={{ transform: "none" }}>
                <div className="text-[#E05624] font-display font-extrabold text-3xl">0{i + 1}</div>
                <div className="mt-3 font-display font-bold text-lg text-[#121316]">{st}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" className="chela-section">
        <div className="chela-container max-w-3xl">
          <h2 className={`${H2} mb-8`}>{c.form_t}</h2>
          <ApplyForm c={c} t={t} />
        </div>
      </section>

      <section className="chela-section bg-[#FFF1EC]">
        <div className="chela-container grid gap-6 md:grid-cols-2">
          {c.resp.map(([title, items], i) => (
            <div key={i} data-testid={`join-resp-${i}`} className="chela-card rounded-3xl p-6" style={{ transform: "none" }}>
              <h3 className="font-display font-bold text-xl text-[#121316]">{title}</h3>
              <ul className="mt-4 space-y-2">
                {items.map((it, j) => <li key={j} className="flex gap-2.5 text-sm text-[#3A3D45]"><Check size={16} className={`mt-0.5 shrink-0 ${i === 2 ? "text-[#C0392B]" : "text-[#E05624]"}`} /> {it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
